// copied from apps/crm; consolidate into a shared package in S5 slice 4
// An in-browser stand-in for services/api behind the fetch seam of
// @aiw/api-client, seen from the installer surface. Same routes, same
// envelopes, same case contract; the role filter mirrors the org_id RLS.
// Routes the live API does not have yet (my-jobs, work-orders, statement)
// are served here and listed in the S6 hand-off.
import { retainLatestWorkRevisions, type Case, type ProposalResult, type SaveWorkRevisionInput, type Verdict, type WorkRevision } from "@artemis-group/aiw-contracts";
import {InstallerPriceCollection} from '@aiw/config';
import * as fx from "./fixtures";

const decided = new Map<string, Verdict["decision"]>();
const actions: Record<string, unknown>[] = [];
let workRevisions: WorkRevision[] = structuredClone(fx.sharedWorkRevisions);
const enc = new TextEncoder();

const json = (data: unknown, status = 200) => new Response(JSON.stringify(data), { status, headers: { "content-type": "application/json" } });
const ok = (data: unknown) => json({ ok: true, data });
const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));
const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

/** installer_internal sees every Team Artemis job; installer_external its own party's only. */
function visibleJobs(role: fx.MockRole) {
  return role === "internal" ? fx.jobs.filter((j) => j.party_id === null) : fx.jobs.filter((j) => j.party_id === fx.YU.id);
}
const canSee = (role: fx.MockRole, ref: string) => visibleJobs(role).some((j) => j.ref === ref);

export const mockFetch: typeof fetch = async (input, init) => {
  const url = new URL(typeof input === "string" ? input : input instanceof URL ? input.href : input.url, "http://mock.local");
  const method = (init?.method ?? "GET").toUpperCase();
  const path = url.pathname;
  const role = fx.mockRole();
  const me = fx.me(role);
  await delay(120);

  if (path === "/api/health") return json({ ok: true, reads: "mock", backbone: "mock", uploads: "mock" });
  if (path === "/api/me") return json(me);

  if (path === "/api/my-jobs") return ok(visibleJobs(role));
  if (path === "/api/installer-team") return role === "external" ? ok(fx.installerTeam) : json({ error: "company managers only" }, 403);
  if (path === "/api/installer-team/work") return role === "external" ? ok(fx.installerTeamWork) : json({ error: "company managers only" }, 403);
  if(path==='/api/installer-price-form')return role==='external'?ok({party:{id:fx.YU.id,name:fx.YU.name,categories:['Hybrid','Laminate']},books:[],config:InstallerPriceCollection.defaults}):json({error:'company managers only'},403);

  let m = path.match(/^\/api\/jobs\/([^/]+)\/installs$/);
  if (m) { if (!canSee(role, m[1]!)) return json({ error: "not your job" }, 403); return ok(fx.installs.filter((i) => i.job_ref === m![1])); }
  m = path.match(/^\/api\/jobs\/([^/]+)$/);
  if (m) { if (!canSee(role, m[1]!)) return json({ error: "not your job" }, 403); const r = fx.jobs.find((j) => j.ref === m![1]); return r ? ok(r) : json({ error: "no such job" }, 404); }

  m = path.match(/^\/api\/work-records\/by-reference\/([^/]+)$/);
  if (m) {
    const ref = decodeURIComponent(m[1]!);
    if (!canSee(role, ref)) return json({ error: "not your job" }, 403);
    return fx.sharedWorkRecord.job_ref === ref ? ok(fx.sharedWorkRecord) : json({ error: "no shared record" }, 404);
  }
  m = path.match(/^\/api\/work-records\/([^/]+)\/revisions$/);
  if (m) {
    if (m[1] !== fx.sharedWorkRecord.id || !fx.sharedWorkRecord.job_ref || !canSee(role, fx.sharedWorkRecord.job_ref)) return json({ error: "not your work record" }, 403);
    return ok(workRevisions.filter((revision) => revision.work_id === m![1]));
  }

  m = path.match(/^\/api\/work-orders\/([^/]+)$/);
  if (m) {
    if (!canSee(role, m[1]!)) return json({ error: "not your job" }, 403);
    const wo = fx.workOrders.find((w) => w.job_ref === m![1]);
    if (!wo) return json({ error: "not published" }, 404);
    // Rates never leave the server for an internal crew.
    return ok(role === "internal" ? { ...wo, labour: null } : wo);
  }

  if (path === "/api/statement") return role === "external" ? ok(fx.statement) : json({ error: "statements exist for external parties only" }, 403);

  if (path === "/api/propose" && method === "POST") {
    const body = JSON.parse(String(init?.body ?? "{}")) as { command: string; args: Record<string, unknown>; idempotency_key: string };
    const action_id = uuid();
    if (body.command === "save_work_revision") {
      const args = body.args as SaveWorkRevisionInput;
      if (args.work_id !== fx.sharedWorkRecord.id || !fx.sharedWorkRecord.job_ref || !canSee(role, fx.sharedWorkRecord.job_ref)) {
        const res: ProposalResult = { status: "denied", action_id, reason: "This work record is not assigned to your installer account" };
        return json(res);
      }
      const duplicate = workRevisions.find((revision) => revision.owner_user_id === me.user.id && revision.idempotency_key === args.idempotency_key);
      if (duplicate) {
        const res: ProposalResult = { status: "executed", action_id, result: { ...duplicate, duplicate: true } };
        return json(res);
      }
      const own = workRevisions.filter((revision) => revision.owner_user_id === me.user.id).sort((a, b) => b.save_number - a.save_number)[0] ?? null;
      if ((own?.id ?? null) !== args.expected_latest_revision_id) {
        const res: ProposalResult = { status: "denied", action_id, reason: "Your saved version changed. Reload before saving." };
        return json(res);
      }
      const source = args.source_revision_id ? workRevisions.find((revision) => revision.id === args.source_revision_id) ?? null : null;
      if (args.source_revision_id && !source) {
        const res: ProposalResult = { status: "denied", action_id, reason: "The source revision is no longer retained." };
        return json(res);
      }
      const saved: WorkRevision = {
        id: uuid(), work_id: args.work_id, owner_user_id: me.user.id,
        owner_name: me.party.name, save_number: (own?.save_number ?? 0) + 1,
        saved_at: new Date().toISOString(), device: args.device,
        idempotency_key: args.idempotency_key, content_hash: "c".repeat(64),
        source: source ? {
          revision_id: source.id, owner_user_id: source.owner_user_id, owner_name: source.owner_name,
          save_number: source.save_number, saved_at: source.saved_at, content_hash: source.content_hash,
        } : null,
        document: structuredClone(args.document),
      };
      workRevisions = retainLatestWorkRevisions([...workRevisions, saved]);
      actions.unshift({ id: action_id, command: body.command, status: "executed", at: saved.saved_at, args, actor: me.actor, surface: "installer", radius: 2, policy: "auto" });
      const res: ProposalResult = { status: "executed", action_id, result: { ...saved, duplicate: false } };
      return json(res);
    }
    if (body.command === "complete_work_order" && body.args.kind === "completion" && !(Array.isArray(body.args.photos) && body.args.photos.length)) {
      const res: ProposalResult = { status: "denied", action_id, reason: "a completion report needs at least one photo" };
      return json(res);
    }
    actions.unshift({ id: action_id, command: body.command, status: "executed", at: new Date().toISOString(), args: body.args, actor: me.actor, surface: "installer", radius: 2, policy: "auto" });
    const res: ProposalResult = { status: "executed", action_id, result: { ok: true, report_id: uuid(), kind: body.args.kind } };
    return json(res);
  }
  if (path === "/api/decide" && method === "POST") {
    const v = JSON.parse(String(init?.body ?? "{}")) as Pick<Verdict, "case_id" | "decision" | "reason">;
    const k = fx.cases.find((c) => c.id === v.case_id);
    if (!k) return json({ error: "no such case" }, 404);
    decided.set(k.id, v.decision);
    actions.unshift({ id: k.action_id, command: k.command, status: v.decision === "approve" ? "executed" : v.decision === "reject" ? "rejected" : "pending", at: new Date().toISOString(), case_id: k.id, actor: me.actor, surface: "installer", radius: k.radius, policy: "ask", reason: v.reason ?? null });
    const res: ProposalResult = v.decision === "approve" ? { status: "executed", action_id: k.action_id, result: { ok: true } } : v.decision === "reject" ? { status: "denied", action_id: k.action_id, reason: v.reason ?? "declined" } : { status: "pending", action_id: k.action_id, case_id: k.id };
    return json(res);
  }
  m = path.match(/^\/api\/cases\/([^/]+)$/);
  if (m && m[1] !== "stream") { const k = fx.cases.find((c) => c.id === m![1]); return k ? json(withStatus(k)) : json({ error: "no such case" }, 404); }
  if (path === "/api/actions") return json({ actions });

  if (path === "/api/cases/stream") {
    const signal = init?.signal;
    const mine = role === "external" ? fx.cases : []; // the offer is addressed to YU Flooring
    const stream = new ReadableStream<Uint8Array>({
      async start(ctrl) {
        for (const k of mine) {
          if (decided.has(k.id)) continue;
          ctrl.enqueue(enc.encode(`event: case\nid: ${k.id}\ndata: ${JSON.stringify(k)}\n\n`));
          await delay(200);
        }
        const timer = setInterval(() => {
          if (signal?.aborted) { clearInterval(timer); try { ctrl.close(); } catch { /* closed */ } return; }
          try { ctrl.enqueue(enc.encode(`event: ping\ndata: ${new Date().toISOString()}\n\n`)); } catch { clearInterval(timer); }
        }, 5000);
      },
    });
    return new Response(stream, { status: 200, headers: { "content-type": "text/event-stream" } });
  }

  if (path === "/api/upload" && method === "POST") {
    const form = init?.body instanceof FormData ? init.body : null;
    const file = form?.get("file");
    const scope = String(form?.get("scope") ?? "misc");
    const name = file instanceof File ? file.name : "photo.jpg";
    const type = file instanceof File ? file.type : "image/jpeg";
    const size = file instanceof File ? file.size : 0;
    const p = `${scope}/${me.actor.role}/${new Date().toISOString().slice(0, 10)}/${uuid()}.${type.split("/")[1] ?? "jpg"}`;
    const previewUrl = file instanceof File ? URL.createObjectURL(file) : `http://mock.local/blob/${p}`;
    return json({ path: p, url: previewUrl, size, content_type: type, name });
  }

  return json({ error: `mock: no route ${method} ${path}` }, 404);
};

function withStatus(k: Case): Case {
  return decided.has(k.id) ? { ...k, status: "decided" } : k;
}
